'use client';

import { useRef, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';

/**
 * The booking page's chat: a question box that talks to the same receptionist
 * the phone does. Sits BESIDE the booking form, never instead of it — every
 * refusal and every limit points back at the form, which keeps working.
 *
 * Holds only what a browser should: the thread key the server minted (an
 * unguessable UUID that reaches its own thread and nothing else) and the
 * messages on screen. Nothing here decides what the receptionist may do.
 */
interface Msg {
  role: 'you' | 'them';
  text: string;
}

export default function ChatPanel({
  handle,
  businessName,
  greeting,
  postcode,
}: {
  handle: string;
  businessName: string;
  greeting: string;
  /** What the customer typed on the first step, so times offered here are reachable. */
  postcode: string;
}) {
  const [open, setOpen] = useState(false);
  const [threadKey, setThreadKey] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([
    { role: 'them', text: greeting || `Hi, this is ${businessName}'s assistant. Ask me anything about the work or the times, or book below.` },
  ]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    setNote(null);
    setMessages((m) => [...m, { role: 'you', text }]);
    setBusy(true);
    try {
      const res = await fetch(`/api/public/booking/${handle}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threadKey, message: text, postcode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // The server's own sentence: "busy, book below" or "could not answer".
        setNote(data.message || 'The chat could not answer just now. You can still book a time below.');
        return;
      }
      if (data.threadKey) setThreadKey(data.threadKey);
      setMessages((m) => [...m, { role: 'them', text: data.reply }]);
      requestAnimationFrame(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight }));
    } catch {
      setNote('The chat could not answer just now. Check your connection, or book a time below.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="pubk-chat-launch">
        <Button variant="ghost" size="md" leadingIcon={MessageCircle} onClick={() => setOpen(true)}>
          Ask a question
        </Button>
      </div>
    );
  }

  return (
    <div className="pubq-card pubk-chat" role="region" aria-label={`Chat with ${businessName}`}>
      <div className="pubk-chat-head">
        <span className="t-body-md">{businessName}</span>
        <button type="button" className="pubk-chat-close" aria-label="Close chat" onClick={() => setOpen(false)}>
          <Icon icon={X} size={16} />
        </button>
      </div>
      <div className="pubk-chat-list" ref={listRef}>
        {messages.map((m, i) => (
          <div key={i} className={`pubk-chat-msg pubk-chat-msg--${m.role}`}>{m.text}</div>
        ))}
        {busy && <div className="pubk-chat-msg pubk-chat-msg--them pubk-muted">…</div>}
      </div>
      {note && <div className="pubq-error t-body-sm" role="alert">{note}</div>}
      <form
        className="pubk-chat-form"
        onSubmit={(e) => { e.preventDefault(); void send(); }}
      >
        <input
          className="pubq-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a question…"
          maxLength={600}
          disabled={busy}
        />
        <Button size="md" type="submit" disabled={busy || !draft.trim()}>Send</Button>
      </form>
    </div>
  );
}
